/**
 * Check Your Email Page
 *
 * Intermediate page shown after successful registration.
 * Instructs user to check their email for verification link.
 * Includes resend functionality with cooldown to prevent spam.
 */

import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import {
  Container,
  Paper,
  Title,
  Text,
  Button,
  Stack,
  Center,
  Alert,
} from '@mantine/core';
import { IconMailOpened, IconCheck, IconAlertCircle } from '@tabler/icons-react';
import { supabase } from '@/lib/supabase';

const RESEND_COOLDOWN_SECONDS = 60;

export const CheckYourEmailPage: React.FC = () => {
  const location = useLocation();
  const email = (location.state as { email?: string })?.email || '';
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  // Cooldown timer
  useEffect(() => {
    if (cooldownRemaining > 0) {
      const timer = setTimeout(() => {
        setCooldownRemaining(cooldownRemaining - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldownRemaining]);

  const handleResend = async () => {
    if (!email || cooldownRemaining > 0) return;

    setIsResending(true);
    setResendSuccess(false);
    setResendError(null);

    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
      });

      if (error) {
        setResendError(error.message);
      } else {
        setResendSuccess(true);
        setCooldownRemaining(RESEND_COOLDOWN_SECONDS);
      }
    } catch (err) {
      setResendError('Failed to resend verification email');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <Container maw={{ base: '100%', xs: 420 }} px={{ base: 'md', xs: 0 }} my={40}>
      <Center>
        <Stack gap="md" w="100%">
          <Title ta="center">Check Your Email</Title>

          <Paper withBorder p={30} mt={30} radius="md">
            <Stack gap="md" align="center">
              <IconMailOpened size={64} color="var(--mantine-color-blue-6)" />

              <Alert color="blue" w="100%">
                <Text size="sm">
                  We've sent a verification link to <strong>{email || 'your email'}</strong>
                </Text>
              </Alert>

              <Text size="sm" ta="center">
                Please check your inbox and click the verification link to
                activate your account. The link will expire in 24 hours.
              </Text>

              {resendSuccess && (
                <Alert icon={<IconCheck size={16} />} color="green" w="100%">
                  Verification email sent! Check your inbox.
                </Alert>
              )}

              {resendError && (
                <Alert icon={<IconAlertCircle size={16} />} color="red" w="100%">
                  {resendError}
                </Alert>
              )}

              {email && (
                <Button
                  variant="light"
                  fullWidth
                  onClick={handleResend}
                  loading={isResending}
                  disabled={cooldownRemaining > 0}
                >
                  {cooldownRemaining > 0
                    ? `Resend in ${cooldownRemaining}s`
                    : "Didn't receive it? Resend email"}
                </Button>
              )}

              <Text size="xs" c="dimmed" ta="center">
                Check your spam folder if you don't see the email.
              </Text>

              <Button component={Link} to="/login" variant="outline" fullWidth>
                Back to Login
              </Button>
            </Stack>
          </Paper>
        </Stack>
      </Center>
    </Container>
  );
};
